import { formatAddressText } from "@/lib/address-format";

/**
 * Parses a Google Places addressComponents array into structured address fields.
 *
 * Includes `subpremise` (APT / UNIT / SUITE numbers returned by Google) as
 * `address2`. When the user types "3270 RICKY DR APT 904" and selects the
 * autocomplete suggestion, Google may return:
 *   street_number: "3270"
 *   route:         "Ricky Dr"
 *   subpremise:    "APT 904"   ← previously discarded; now captured
 */
export function parseGoogleAddressComponents(
  addressComponents: google.maps.places.AddressComponent[],
): {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
} {
  const get = (
    type: string,
    field: "longText" | "shortText" = "longText",
  ): string =>
    addressComponents.find((c) => c.types.includes(type))?.[field] ?? "";

  const streetNumber = get("street_number");
  const route = get("route", "shortText");
  const subpremise = get("subpremise");
  const city = get("locality") || get("sublocality");
  const state = get("administrative_area_level_1", "shortText");
  const zipcode = get("postal_code");

  return {
    address1: formatAddressText(`${streetNumber} ${route}`),
    address2: formatAddressText(subpremise),
    city: formatAddressText(city, "city"),
    state: formatAddressText(state, "state"),
    zipcode: formatAddressText(zipcode, "zipcode"),
  };
}
